import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { getRouteShell, normalizeRouteShellPath } from "../utils/routeShell.js";
import { hasThirdPartyAnalyticsPresent } from "../utils/thirdPartyAnalyticsIsolation.js";

export const PRIVATE_APP_NAVIGATION_MARKER = "__offlico_private_app_navigation__";

export function isPrivateAppPathname(pathname = "/") {
  const normalizedPathname = normalizeRouteShellPath(pathname);
  if (normalizedPathname === "/app" || normalizedPathname.startsWith("/app/")) return true;
  // The Team plan's invite page carries the invite token in its address, so it
  // gets a document without third-party analytics too (e.g. back from /signin).
  return normalizedPathname === "/team" || normalizedPathname.startsWith("/team/");
}

export function resolveInitialRouteShell() {
  if (typeof window === "undefined") return "app";
  const declaredShell = window.__OFFLICO_INITIAL_ROUTE_SHELL__;
  if (declaredShell === "marketing" || declaredShell === "app") return declaredShell;

  const documentShell = document.documentElement.getAttribute("data-route-shell");
  if (documentShell === "marketing" || documentShell === "app") return documentShell;

  return getRouteShell(window.location.pathname || "/");
}

function replaceCurrentDocument(target) {
  window.location.replace(target);
}

export default function PrivateAppDocumentBoundary({
  children,
  initialRouteShell,
  replaceDocument = replaceCurrentDocument,
}) {
  const location = useLocation();
  const initialRouteShellRef = useRef(initialRouteShell || resolveInitialRouteShell());
  const target = `${location.pathname}${location.search}${location.hash}`;
  const requiresFreshDocument =
    isPrivateAppPathname(location.pathname) &&
    (initialRouteShellRef.current === "marketing" || hasThirdPartyAnalyticsPresent());

  useLayoutEffect(() => {
    if (!requiresFreshDocument || typeof window === "undefined") return;
    if (window[PRIVATE_APP_NAVIGATION_MARKER] === target) return;

    window[PRIVATE_APP_NAVIGATION_MARKER] = target;
    replaceDocument(target);
  }, [replaceDocument, requiresFreshDocument, target]);

  if (requiresFreshDocument) return null;
  return children;
}
